<script>
import * as FileDownload from 'downloadjs';
import { groupBy, orderBy, trim } from 'lodash';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import { FilterComponent, GroupBy, SortComponent, TableList, VisibleProperty } from '../components';
import { columns } from '../config';

export default {
	name: "dailyCloseoutReport",
	components: {
		SortComponent,
		VisibleProperty,
		GroupBy,
		FilterComponent,
		TableList
	},
	data() {
		return {
			branch: [],
			subprograms: [],
			visible: {
				cols: []
			},
			show_pdf: false,
			pdf_generate: false,
			excel_generate: false,
			src: '',
			model: {
				branch_id: undefined,
				acps_id: undefined,
				dateFrom: moment().format('YYYY-MM-DD'),
				dateTo: moment().format('YYYY-MM-DD'),
				sort: [{ col: "payment_date", text: "Invoice Date", order: "asc" }, { col: "invoice_number", text: "Receipt #", order: "asc" }],
				groupBy: undefined,
				visible: [],
				filters: [
				]
			},
			loading: false
		}
	},
	computed: {
		...mapState('dailyCloseoutReport', ['resources', 'pagination']),
		prepareResource() {
			let tmpResource = this.resources;
			if (this.model.groupBy) {
				return groupBy(tmpResource, this.model.groupBy)
			}

			return tmpResource;
		},
		columnsTable() {
			return this.visible.cols
				.map(el => ({
					key: el.key || el.col,
					label: this.$t(`dailyCloseout.${el.label}`),
					className: el.className,
					slot: el.slot,
					numberFormat: el.numberFormat
				}));
		}
	},
	created() {
		const filterOption = localStorage.getItem('daily_closeout_report_filter')
		if (filterOption) {
			this.model.filters = JSON.parse(filterOption)
		}

		const visibleColOrder = localStorage.getItem('daily_closeout_report_visible_order')
		if (visibleColOrder) {
			this.visible.cols = JSON.parse(visibleColOrder)
		} else {
			this.visible.cols = columns()
		}
		const visibleCol = localStorage.getItem('daily_closeout_report_visible')
		if (visibleCol) {
			this.model.visible = JSON.parse(visibleCol)
		} else {
			this.model.visible = this.columnsTable.map(el => el.key)
		}
	},
	mounted() {
		this.fetchResource()
	},
	methods: {
		...mapActions('dailyCloseoutReport', ['fetch', 'pdf', 'getBranch', 'toExcel', 'getSubProgram']),
		onSearch(attribute = {}) {
			this.loading = true
			this.fetch(Object.assign({}, this.model, attribute))
				.finally(() => this.loading = false)

		},
		onPdf(attribute = {}) {
			this.show_pdf = true
			this.pdf_generate = true
			this.src = ''
			this.pdf(Object.assign({}, this.model, attribute))
				.then(response => {
					this.src = response.data.url
				})
				.finally(() => this.pdf_generate = false)
		},
		onExcel(attribute = {}) {
			this.excel_generate = true
			this.toExcel(Object.assign({}, this.model, attribute))
				.then(response => {
					let filename = trim(
						response.headers['content-disposition']
							.substring(
								response.headers['content-disposition'].indexOf('filename')
							)
							.replace('filename=', ''),
						'"'
					)
					FileDownload(response.data, filename)
				})
				.catch(err => {
					let error = JSON.parse(
						String.fromCharCode.apply(null, new Uint8Array(err.response.data))
					)
					this.$toasted.error(error.message)
				})
				.finally(() => this.excel_generate = false)
		},
		async fetchResource() {
			this.loading = true

			const [branchResponse, subProgramResponse] = await Promise.all([
				this.getBranch(),
				this.getSubProgram()
			]);

			this.branch = branchResponse.data.map(el => ({
				value: el.branch_id,
				label: el.branch_name_en
			}))

			this.subprograms = subProgramResponse.data.map(el => ({
				value: el.acps_id,
				label: el.acps_name_foreign
			}))

			this.loading = false
		},
		formatDisplay(date) {
			return date ? date.format('DD-MMM-YYYY') : '';
		},
		filterOption(input, option) {
			return option.componentOptions.children[0].text
				.toLowerCase()
				.indexOf(input.toLowerCase()) >= 0
		},
	}

}
</script>

<template>
	<div>
		<div class="d-flex justify-content-between ts-mb-3">
			<div>
				<h1 class="page-header ts-mb-0">
					{{ $t('dailyCloseout.pageTitle') }}
				</h1>
			</div>
			<ts-breadcrumb :items="[
				{ text: $t('toHomePage'), href: '/' },
				{
					text: $t('dailyCloseout.pageTitle'),
					active: true
				}
			]" />
		</div>

		<div class="ts-space-y-3">
			<ts-card>
				<ts-card-wrapper>
					<div class="ts-space-y-2">
						<div class="ts-flex ts-space-x-2 ts-border-b ts-pb-2">
							<SortComponent v-model="model" @fetch="onSearch" />
							<GroupBy v-model="model" />
							<VisibleProperty v-model="model" :cols="visible" />
							<FilterComponent v-model="model" @search="onSearch" />
						</div>
						<div>
							<div class="ts-flex ts-space-x-2 ts-items-center">
								<a-select v-model="model.branch_id" :options="branch" :allowClear="true" style="width: 200px;"
									:placeholder="$t('dailyCloseout.selectBranch')"></a-select>
								<a-select v-model="model.acps_id" :options="subprograms" :allowClear="true" mode="multiple" :maxTagCount="1"
									 :maxTagTextLength="20" style="width: 280px;"
									:placeholder="$t('dailyCloseout.selectSubProgram')" :showSearch="true"
									:filterOption="filterOption"></a-select>
								<a-date-picker v-model="model.dateFrom" :format="formatDisplay" valueFormat="YYYY-MM-DD"
									:allowClear="false" :placeholder="$t('dailyCloseout.dateFrom')" class="ts-w-32" />
								<b><i class="fas fa-arrow-right"></i></b>
								<a-date-picker v-model="model.dateTo" :format="formatDisplay" valueFormat="YYYY-MM-DD"
									:allowClear="false" :placeholder="$t('dailyCloseout.dateTo')" class="ts-w-32" />
								<button class="ts-bg-blue-500 ts-text-white ts-px-3 ts-py-2 ts-rounded focus:ts-outline-none"
									@click.prevent="onSearch">
									<a-icon type="search" />
									{{ $t('dailyCloseout.search') }}
								</button>
								<button @click.prevent="onPdf"
									class="ts-border ts-border-red-500 ts-text-red-500 ts-px-6 ts-rounded ts-cursor-pointer focus:ts-outline-none"
									style="padding: 5px 20px;">
									<i class="fas fa-file-pdf" style="font-size: 16px;"></i>
								</button>
								<button @click.prevent="onExcel" :disabled="excel_generate"
									class="ts-border ts-border-green-500 ts-text-green-500 ts-px-6 ts-rounded ts-cursor-pointer focus:ts-outline-none"
									style="padding: 5px 20px;">
									<i class="far fa-file-excel" style="font-size: 16px;" v-show="!excel_generate"></i>
									<i class="fas fa-spinner fa-spin" v-show="excel_generate"></i>
								</button>
							</div>
						</div>
					</div>
				</ts-card-wrapper>
			</ts-card>
			<ts-card>
				<div>
					<ts-loading-banner :loading="loading">
						<TableList :columns="columnsTable" :visible="model.visible" :data="prepareResource">
							<template #discount="{ row }">
								{{ row.discount_rate }} {{ row.discount_type }}
							</template>
						</TableList>
					</ts-loading-banner>
				</div>
				<div class="d-flex justify-content-between align-items-center p-15">
					<ts-total-list v-model="pagination" />
					<ts-pagination v-model="pagination" @navigate="onSearch"></ts-pagination>
				</div>
			</ts-card>
		</div>

		<a-modal :title="$t('pdf')" width="70%" v-model="show_pdf" :footer="null">
			<ts-preview-pdf v-model="pdf_generate" :src="src" />
		</a-modal>
	</div>
</template>